import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../../components/SideBar/Sidebar';
import * as Body from './Styles';
import Avatar from '../../../assets/images/avatar.png';
import UsuarioIcon from '../../../assets/icons/usuario-icon.svg';
import TecnicoIcon from '../../../assets/icons/tecnico-icon.svg';
import CheckIcon from '../../../assets/icons/check-icon-white.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import Loading from '../../components/Loading/Loading';
import { ModalDeleteUser } from './Modals/ModalDeleteUser';
import HardDelete from './Modals/HardDelete';

import * as Functions from '../../../utils/functions/Functions';
import * as defines from '../../../utils/defines/Defines';
import * as Types from '../../../utils/types/Types';
import { State } from '../../redux/store';

// Improved type definitions
interface UserData {
  idEmpresarial: string;
  idUsuario: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  nomeCompleto: string;
  email: string;
  cargo: number;
  encarregadoDados: number;
}

const EditUser: React.FC = () => {
  const { state } = useLocation();
  const { filial, company, user } = state as {
    filial: Types.FilialInfo;
    company: Types.EmpresaInfo;
    user: UserData;
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selectors
  const userType = useSelector((state: State) => state.userType.userType);
  const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);

  // State management
  const [userData, setUserData] = useState<UserData>({
    ...user,
    fotoUsuario: user.fotoUsuario || ''
  });
  const [loading, setLoading] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);
  const [isEncarregadoDados, setIsEncarregadoDados] = useState(user.encarregadoDados === 1);
  const [nivelAcesso, setNivelAcesso] = useState(user.cargo);

  // Memoized email validation
  const validateEmail = useCallback((email: string) => {
    const isValid = Functions.verifyEmail(email);
    setErrorEmail(!isValid);
    return isValid;
  }, []);

  // Update user handler with improved error handling
  const updateUser = useCallback(() => {
    // Validate required fields
    if (!validateEmail(userData.email)) {
      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 0,
        title: 'Erro',
        text: 'Por favor, insira um email válido'
      });
      return;
    }

    setLoading(true);
    
    const updatedFields: Partial<UserData> = {
      idUsuario: userData.idUsuario,
      idEmpresarial: userData.idEmpresarial,
      email: userData.email,
      celular: userData.celular,
      ddd: userData.ddd,
      nomeCompleto: userData.nomeCompleto,
      cargo: nivelAcesso,
      encarregadoDados: isEncarregadoDados ? 1 : 0
    };

    axios.put(
      `${defines.apiURL}/api/UpdateUser?userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(filial.idFilial)}`,
      updatedFields,
      { withCredentials: true }
    )
    .then(() => {
      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        title: '',
        text: 'Dados do usuário alterados com sucesso'
      });
      setLoading(false);
    })
    .catch((error) => {
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        dispatch,
        error.response.data.content
      );
      setLoading(false);
    });
  }, [
    userData, 
    nivelAcesso, 
    isEncarregadoDados, 
    userType, 
    filial.idFilial, 
    navigate, 
    dispatch, 
    validateEmail
  ]);

  // Hard delete user handler
  const hardDeleteUser = useCallback(() => {
    setLoading(true);
    axios.delete(
      `${defines.apiURL}/api/UsuarioCRUD?idFilial=${Functions.ReplaceSpecialCharacters(filial.idFilial)}&userTypeRequest=${userType}&id=${Functions.ReplaceSpecialCharacters(userData.idUsuario)}`,
      { withCredentials: true }
    )
    .then(() => {
      setLoading(false);
      setHardDelete(false);
      navigate('/usuarios', { state: { filial, company } });
    })
    .catch((error) => {
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        dispatch,
        error.response.data.content
      );
      setLoading(false);
    });
  }, [filial, company, userData.idUsuario, userType, navigate, dispatch]);

  return (
    <Body.Wrapper>
      <Sidebar
        type={userType}
        content={defines.ContentSidebar.MANAGE_COMPANY}
        tituloPagina='Editar Usuário'
        backAction={() => {
          navigate('/usuarios', { state: { filial, company } });
        }}
      />

      <Body.Container>
        {/* User Photo */}
        <div className="flex justify-center items-center min-h-[300px]">
          <Body.Photo src={userData.fotoUsuario || Avatar} />
        </div>

        {/* User Details */}
        <Body.Row>
          <Input
            text={userData.nomeCompleto}
            setText={(value: string) => setUserData(prev => ({ ...prev, nomeCompleto: value }))}
            placeholder="Nome Completo"
            title="Nome Completo"
            disabled
          />
        </Body.Row>

        <Body.Row>
          <Input
            text={userData.email}
            setText={(value: string) => {
              setUserData(prev => ({ ...prev, email: value }));
              validateEmail(value);
            }}
            placeholder="Email"
            title="Email"
            disabled
          />
        </Body.Row>

        <Body.Row>
          <Input
            width={'20%'}
            text={userData.ddd}
            setText={(value: string) => setUserData(prev => ({ ...prev, ddd: value }))}
            placeholder="DDD"
            title="DDD"
            limit={3}
            disabled
          />
          <Input
            width={'80%'}
            text={userData.celular}
            setText={(value: string) => setUserData(prev => ({ ...prev, celular: value }))}
            placeholder="Telefone"
            title="Telefone"
            limit={9}
            disabled
          />
        </Body.Row>

        {errorEmail && (
          <Body.TextError>
            Formato de email inválido ex: email@email.com
          </Body.TextError>
        )}

        {/* User Role Selection */}
        <div className="flex justify-center items-center min-h-[120px]">
          <Body.Row>
            <Body.Card
              onClick={() => setNivelAcesso(defines.CargoEmpresarial['Padrão'])}
              style={{
                border: nivelAcesso === defines.CargoEmpresarial['Padrão'] 
                  ? `2px solid ${defines.BlueTec}` 
                  : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <Body.CardIcon src={UsuarioIcon} />
              <Body.CardTitle>Padrão</Body.CardTitle>
            </Body.Card>
            <Body.Card
              onClick={() => setNivelAcesso(defines.CargoEmpresarial['Administrador'])}
              style={{
                border: nivelAcesso === defines.CargoEmpresarial['Administrador'] 
                  ? `2px solid ${defines.BlueTec}` 
                  : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <Body.CardIcon src={TecnicoIcon} />
              <Body.CardTitle>Administrador</Body.CardTitle>
            </Body.Card>
          </Body.Row>
        </div>

        {/* Data Manager Toggle */}
        <div 
          className="flex flex-row items-center p-5 cursor-pointer"
          onClick={() => setIsEncarregadoDados(!isEncarregadoDados)}
        >
          <Body.ToggleBtn active={isEncarregadoDados}>
            {isEncarregadoDados && <Body.IconCheckbox src={CheckIcon} />}
          </Body.ToggleBtn>
          <Body.Text className="w-[calc(100%-40px)]">
            Encarregado de Dados
          </Body.Text>
        </div>

        {/* Action Buttons */}
        {acessLevel !== defines.CargoTectrol['Padrão'] && (
          <>
            <Body.Row>
              <Button
                text="Salvar"
                action={updateUser}
                width={155}
                loading={loading}
              />
              <Button
                text="Desvincular usuário"
                action={() => setModalDelete(true)}
                width={155}
                color="yellow"
                loading={loading}
              />
            </Body.Row>
            <Body.Row>
              <Button
                text="Deletar usuário"
                action={() => setHardDelete(true)}
                width={155}
                color="red"
                loading={loading}
              />
            </Body.Row>
          </>
        )}

        {/* Modals and Loading */}
        {loading && <Loading />}
        {modalDelete && (
          <ModalDeleteUser
            toggle={setModalDelete}
            idUsuario={userData.idUsuario}
            nome={userData.nomeCompleto}
            action={() => {
              navigate('/usuarios', { state: { filial, company } });
            }}
            nomeFilial={filial.nomeFilial}
          />
        )}
        {hardDelete && (
          <HardDelete
            toggle={setHardDelete}
            idUsuario={userData.idUsuario}
            nome={userData.nomeCompleto}
            action={hardDeleteUser}
            nomeFilial={filial.nomeFilial}
          />
        )}
      </Body.Container>
    </Body.Wrapper>
  );
};

export default EditUser;